import { useState, useRef, useEffect, useMemo, type CSSProperties } from "react";
import { Avatar } from "./primitives";
import type { Persona } from "./primitives";
import { getAgentPersona } from "./cockpit/CockpitHeader";
import { useWorkspaceMembers } from "../hooks/use-workspace-members";
import { useListTree } from "../../lib/hooks/use-kuvio";
import { useAuth } from "../../lib/hooks/use-auth";

interface MemberPickerProps {
  /** Selected assignee — a user_id, an agent node_id, or null (unassigned). */
  value: string | null;
  onChange: (id: string | null) => void;
  /** When true, agent nodes are offered alongside members (for delegation). */
  allowAgents?: boolean;
  placeholder?: string;
}

interface Option {
  id: string | null;
  label: string;
  sublabel?: string;
  kind: "unassigned" | "user" | "agent";
  persona: Persona;
  avatarUrl?: string | null;
  isYou?: boolean;
}

/**
 * Sprint 7 — searchable assignee picker. Lists workspace members (and, when
 * `allowAgents`, agent nodes for delegation). The current user sorts to the top
 * with a "(You)" suffix; "Unassigned" leads the list and clears the selection.
 */
export function MemberPicker({ value, onChange, allowAgents = true, placeholder = "Unassigned" }: MemberPickerProps) {
  const { user, currentWorkspaceId } = useAuth();
  const members = useWorkspaceMembers();
  const tree = useListTree(allowAgents ? currentWorkspaceId ?? undefined : undefined);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const options = useMemo<Option[]>(() => {
    const out: Option[] = [];
    const memberOpts: Option[] = (members.data?.members ?? [])
      .filter((m) => m.status !== "pending")
      .map((m) => ({
        id: m.id,
        label: m.name || m.email,
        sublabel: m.name ? m.email : undefined,
        kind: "user" as const,
        persona: "gray" as Persona,
        isYou: m.id === user?.id,
      }));
    // Current user floats to the top with a (You) suffix.
    memberOpts.sort((a, b) => (a.isYou === b.isYou ? a.label.localeCompare(b.label) : a.isYou ? -1 : 1));
    out.push(...memberOpts);

    if (allowAgents) {
      const agentOpts: Option[] = (tree.data ?? [])
        .filter((n) => n.type === "agent")
        .map((n) => ({
          id: n.id,
          label: n.name,
          sublabel: "Agent",
          kind: "agent" as const,
          persona: getAgentPersona(n),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      out.push(...agentOpts);
    }
    return out;
  }, [members.data, tree.data, allowAgents, user?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.sublabel?.toLowerCase().includes(q),
    );
  }, [options, query]);

  const selected = value === null ? null : options.find((o) => o.id === value);

  function choose(id: string | null) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  const triggerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    fontSize: 13,
    padding: "7px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    textAlign: "left",
  };

  const dropdownStyle: CSSProperties = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    maxHeight: 260,
    overflowY: "auto",
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-md)",
    padding: 4,
    zIndex: 200,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  };

  const searchStyle: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "6px 8px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    marginBottom: 4,
    fontFamily: "var(--font-sans)",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" style={triggerStyle} onClick={() => setOpen((v) => !v)}>
        {selected ? (
          <>
            <Avatar persona={selected.persona} size="xs" name={selected.label} imageUrl={selected.avatarUrl ?? undefined} />
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected.label}{selected.isYou ? " (You)" : ""}
            </span>
          </>
        ) : (
          <span style={{ flex: 1, color: "var(--color-text-tertiary)" }}>{placeholder}</span>
        )}
        <i className="ti ti-selector" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
      </button>

      {open && (
        <div style={dropdownStyle}>
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people or agents…"
            style={searchStyle}
          />
          <Row
            option={{ id: null, label: "Unassigned", kind: "unassigned", persona: "gray" }}
            selected={value === null}
            onClick={() => choose(null)}
          />
          {filtered.map((o) => (
            <Row key={`${o.kind}:${o.id}`} option={o} selected={o.id === value} onClick={() => choose(o.id)} />
          ))}
          {filtered.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "8px 10px" }}>
              No matches.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ option, selected, onClick }: { option: Option; selected: boolean; onClick: () => void }) {
  const rowStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "6px 8px",
    border: "none",
    background: selected ? "var(--color-background-secondary)" : "transparent",
    borderRadius: "var(--border-radius-sm)",
    cursor: "pointer",
    fontSize: 13,
    textAlign: "left",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
  };
  return (
    <button type="button" style={rowStyle} onClick={onClick}>
      {option.kind === "unassigned" ? (
        <span style={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-tertiary)" }}>
          <i className="ti ti-user-off" style={{ fontSize: 14 }} aria-hidden="true" />
        </span>
      ) : (
        <Avatar persona={option.persona} size="xs" name={option.label} imageUrl={option.avatarUrl ?? undefined} />
      )}
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {option.label}{option.isYou ? " (You)" : ""}
        {option.sublabel && (
          <span style={{ color: "var(--color-text-tertiary)", fontSize: 11, marginLeft: 6 }}>{option.sublabel}</span>
        )}
      </span>
      {selected && <i className="ti ti-check" style={{ fontSize: 14, color: "var(--color-text-info)" }} aria-hidden="true" />}
    </button>
  );
}
